import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/feature/JsonLd';
import { articleContent, articles, articleSeoData } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hipobuyspread.autos';

function buildArticleJsonLd(article: typeof articleContent[string], slug: string, seo: typeof articleSeoData[string]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: seo?.description || article.sections[0]?.content?.replace(/<[^>]*>/g, '').substring(0, 160) || '',
    image: article.headerImage,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: article.author,
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Hipobuy Spreadsheet Hub',
      url: siteUrl,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/${slug}`,
    },
  };
}

const relatedImages: Record<string, string> = {
  'hipobuy-spreadsheet-guide': 'https://readdy.ai/api/search-image?query=Clean%20modern%20product%20photography%20of%20a%20laptop%20next%20to%20styled%20fashion%20accessories%20on%20a%20warm%20terracotta%20surface%2C%20soft%20natural%20window%20light%2C%20editorial%20aesthetic%2C%20minimal%20composition%2C%20warm%20neutral%20tones%2C%20lifestyle%20workspace%20scene&width=600&height=420&seq=related-card-guide-v2&orientation=landscape',
  'hipobuy-spreadsheet-shoes': 'https://readdy.ai/api/search-image?query=Gorgeous%20close%20up%20of%20premium%20sneakers%20arranged%20on%20a%20warm%20cream%20surface%20with%20natural%20sunlight%20casting%20gentle%20shadows%2C%20editorial%20sneaker%20photography%2C%20clean%20minimal%20background%2C%20texture%20details%20visible%2C%20harmonious%20warm%20palette%2C%20no%20faces&width=600&height=420&seq=related-card-shoes-v2&orientation=landscape',
  'hipobuy-spreadsheet-clothing': 'https://readdy.ai/api/search-image?query=Beautiful%20flat%20lay%20of%20folded%20premium%20hoodies%20in%20warm%20earth%20tones%20neatly%20stacked%20on%20a%20linen%20surface%2C%20soft%20natural%20lighting%20from%20side%2C%20editorial%20fashion%20catalog%20aesthetic%2C%20fabric%20texture%20details%2C%20clean%20composition%2C%20warm%20neutral%20palette&width=600&height=420&seq=related-card-clothing-v2&orientation=landscape',
  'hipobuy-spreadsheet-accessories': 'https://readdy.ai/api/search-image?query=Elegant%20arrangement%20of%20fashion%20accessories%20including%20a%20leather%20watch%20sunglasses%20and%20a%20minimalist%20bag%20on%20warm%20cream%20linen%2C%20soft%20natural%20lighting%2C%20editorial%20product%20photography%2C%20clean%20harmonious%20composition%2C%20warm%20neutral%20aesthetic&width=600&height=420&seq=related-card-accessories-v2&orientation=landscape',
  'hipobuy-vs-kakobuy-spreadsheet': 'https://readdy.ai/api/search-image?query=Split%20composition%20showing%20premium%20accessories%20arranged%20in%20two%20contrasting%20layouts%20on%20a%20warm%20neutral%20background%2C%20editorial%20comparison%20aesthetic%2C%20clean%20product%20photography%2C%20balanced%20visual%20composition%2C%20soft%20natural%20lighting&width=600&height=420&seq=related-card-compare-v2&orientation=landscape',
};

const relatedFallbackImage = 'https://readdy.ai/api/search-image?query=Stylish%20product%20photography%20of%20fashion%20items%20including%20sneakers%20and%20accessories%20neatly%20arranged%20on%20a%20warm%20beige%20surface%2C%20soft%20natural%20lighting%2C%20editorial%20quality%2C%20clean%20minimal%20composition%2C%20harmonious%20warm%20neutral%20tones&width=600&height=420&seq=related-card-fallback-v2&orientation=landscape';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleContent[slug] : null;

  const otherArticles = articles.filter(a => a.slug !== slug).slice(0, 3);

  const seo = slug ? articleSeoData[slug] : null;
  const canonicalUrl = slug ? `${siteUrl}/${slug}` : siteUrl;

  if (!article) {
    return (
      <>
        <title>Page Not Found — Hipobuy Spreadsheet Hub</title>
        <meta name="description" content="The page you are looking for does not exist. Browse our Hipobuy spreadsheet guides and resources." />
        <meta name="robots" content="noindex" />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Back to Homepage
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <title>{seo?.title || article.title}</title>
      <meta name="description" content={seo?.description || article.sections[0]?.content?.replace(/<[^>]*>/g, '').substring(0, 160) || ''} />
      <meta name="keywords" content={seo?.keywords || ''} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={seo?.title || article.title} />
      <meta property="og:description" content={seo?.description || article.sections[0]?.content?.replace(/<[^>]*>/g, '').substring(0, 160) || ''} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={article.headerImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo?.title || article.title} />
      <meta name="twitter:description" content={seo?.description || article.sections[0]?.content?.replace(/<[^>]*>/g, '').substring(0, 160) || ''} />
      <meta name="twitter:image" content={article.headerImage} />
      <JsonLd data={buildArticleJsonLd(article, slug || '', seo)} />
      <Navbar />
      <main>
        <article>
          <header className="pt-24 md:pt-28 pb-10 md:pb-14 bg-background-100">
            <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
                  Home
                </Link>
                <span className="text-foreground-400">/</span>
                <span className="text-foreground-700 whitespace-nowrap">{article.category}</span>
                <span className="text-foreground-400">/</span>
                <span className="text-foreground-900 font-medium whitespace-nowrap">{article.title}</span>
              </nav>

              <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-12">
                <div className="w-full lg:w-[65%]">
                  <span className="inline-block px-3 py-1 bg-primary-50 rounded-full text-xs font-semibold text-primary-600 mb-4 whitespace-nowrap">
                    {article.category}
                  </span>
                  <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                    {article.title}
                  </h1>
                  <div className="flex items-center gap-4 mt-4 text-xs text-foreground-500">
                    <span>{article.author}</span>
                    <span>&bull;</span>
                    <span>{article.date}</span>
                  </div>
                </div>

                <div className="w-full lg:w-[35%]">
                  <div className="relative rounded-[32px] overflow-hidden bg-background-200 aspect-[4/3]">
                    <img
                      src={article.headerImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="w-full lg:w-[65%]">
                <div className="prose-custom space-y-8">
                  {article.sections.map((section, idx) => (
                    <section key={idx}>
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 leading-snug">
                        {section.heading}
                      </h2>
                      <div
                        className="text-sm md:text-base text-foreground-700 leading-[1.75]"
                        dangerouslySetInnerHTML={{ __html: section.content }}
                      />
                    </section>
                  ))}
                </div>

                <div className="mt-12 p-6 md:p-8 bg-primary-50 rounded-3xl border border-primary-100">
                  <h3 className="font-heading text-lg font-bold text-primary-700 mb-2">
                    Ready to Start Shopping Smarter?
                  </h3>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                    Access the complete Hipobuy spreadsheet database with organized product categories, QC photos, and direct links. Find better products at better prices.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      Hipobuy Sheet
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-external-link-line text-sm"></i>
                      </div>
                    </a>
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-50 border border-primary-200 rounded-full text-sm font-semibold text-primary-600 hover:bg-primary-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      Back to Homepage
                    </Link>
                  </div>
                </div>
              </div>

              <aside className="w-full lg:w-[35%]">
                <div className="sticky top-24">
                  <div className="bg-background-100 rounded-3xl border border-background-200/60 p-6 md:p-8 mb-6">
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">About This Guide</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      This comprehensive guide covers everything you need to know about Hipobuy spreadsheets — from understanding the basics to mastering advanced shopping strategies.
                    </p>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-2 text-foreground-600">
                        <div className="w-4 h-4 flex items-center justify-center text-primary-500">
                          <i className="ri-check-line text-sm"></i>
                        </div>
                        Complete beginner to advanced
                      </div>
                      <div className="flex items-center gap-2 text-foreground-600">
                        <div className="w-4 h-4 flex items-center justify-center text-primary-500">
                          <i className="ri-check-line text-sm"></i>
                        </div>
                        QC photo guidance included
                      </div>
                      <div className="flex items-center gap-2 text-foreground-600">
                        <div className="w-4 h-4 flex items-center justify-center text-primary-500">
                          <i className="ri-check-line text-sm"></i>
                        </div>
                        Category-specific tips
                      </div>
                    </div>
                  </div>

                  <div className="bg-accent-50 rounded-3xl border border-accent-100 p-6 md:p-8">
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">Quick Navigation</h3>
                    <ul className="space-y-2">
                      {article.sections.slice(0, 6).map((section, idx) => (
                        <li key={idx}>
                          <span className="text-xs text-foreground-600 hover:text-primary-500 transition-colors duration-200 cursor-pointer">
                            {section.heading}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </aside>
            </div>
          </div>

          {otherArticles.length > 0 && (
            <section className="py-14 md:py-16 bg-background-100">
              <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
                  Related Articles
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {otherArticles.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/${a.slug}`}
                      className="group block bg-background-50 rounded-3xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={relatedImages[a.slug] || relatedFallbackImage}
                          alt={a.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <span className="inline-block px-2.5 py-0.5 bg-secondary-100 rounded-full text-xs font-medium text-secondary-700 mb-2 whitespace-nowrap">
                          {a.category}
                        </span>
                        <h3 className="font-heading text-base font-bold text-foreground-950 leading-snug mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                          {a.title}
                        </h3>
                        <p className="text-xs text-foreground-600 line-clamp-2">{a.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}